import { CurationsIcon } from '@c/icons';
import type { ChatDocument, ChatMessageDocument } from '@models/chat';
import { useQuery } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import Chip from '@ui/Chip';
import SafeImage from '@ui/SafeImage';
import ProductPriceRange from '@ui/ProductPriceRange';
import { getOrderById } from '@util/firestore/orders';
import { getProductById } from '@util/firestore/products';
import { getPublicUserDoc, getUserDocument } from '@util/firestore/users';
import {
  cn,
  getOfferStatusBadge,
  offerStateToText,
  timeAgo,
} from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useMemo } from 'react';
import { FlagIcon, ImageIcon, StarIcon } from 'lucide-react';
import Link from 'next/link';
import { formatTimeRemaining } from './MessageOffer';
import { getOffer } from '@util/firestore/offers';

interface MessagePreviewProps {
  onClick: () => void;
  selected?: boolean;
  chat: ChatDocument;
  isSidebar?: boolean;
  chats: ChatDocument[];
  searchQuery?: string;
  activeFilter?: string;
}

const MessagePreview = ({
  selected,
  onClick,
  chat,
  isSidebar,
  chats,
  searchQuery,
  activeFilter,
}: MessagePreviewProps) => {
  const { userDoc } = useAuth();

  const { data: productDoc, isLoading: isProductLoading } = useQuery({
    queryKey: ['product', chat.product_id],
    queryFn: () => getProductById(chat.product_id ?? ''),
    enabled: !!chat.product_id,
  });

  const { data: sellerDoc } = useQuery({
    queryKey: ['publicUser', chat.seller_id],
    queryFn: () =>
      getPublicUserDoc({
        uid: chat.seller_id,
        noCache: userDoc?.roles?.support,
      }),
    enabled: !!chat.seller_id,
  });

  const { data: buyerDoc } = useQuery({
    queryKey: ['publicUser', chat.buyer_id],
    queryFn: () =>
      getPublicUserDoc({
        uid: chat.buyer_id,
        noCache: userDoc?.roles?.support,
      }),
    enabled: !!chat.buyer_id,
  });

  const { data: orderDoc } = useQuery({
    queryKey: ['order', chat.order_id],
    queryFn: () => getOrderById(chat.order_id ?? ''),
    enabled: !!chat.order_id,
  });

  const { data: fullSellerDoc } = useQuery({
    queryKey: ['fullUser', chat.seller_id],
    queryFn: () => getUserDocument({ uid: chat.seller_id }),
    enabled: !!chat.seller_id && !!userDoc?.roles?.support,
  });

  const { data: fullBuyerDoc } = useQuery({
    queryKey: ['fullUser', chat.buyer_id],
    queryFn: () => getUserDocument({ uid: chat.buyer_id }),
    enabled: !!chat.buyer_id && !!userDoc?.roles?.support,
  });
  const { data: offer } = useQuery({
    queryKey: ['offer', chat.offer_id],
    queryFn: () => getOffer(chat.offer_id ?? ''),
    enabled: !!chat.offer_id,
  });

  const getChipColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'offer-accepted';
      case 'declined':
        return 'offer-declined';
      case 'pending':
        return 'offer-pending';
      case 'expired':
        return 'offer-expired';
      default:
        return 'gray';
    }
  };

  const getTitle = () => {
    if (chat.case_num) {
      // pad with 0s
      return `MX Locker Support #${chat.case_num.toString().padStart(5, '0')}`;
    }
    if (chat.is_offer) {
      return productDoc?.title;
    } else if (sellerDoc?.uid === userDoc?.uid) {
      return buyerDoc?.username;
    }
    return sellerDoc?.username;
  };

  const isUnread = useMemo(() => {
    return userDoc?.uid
      ? !!chat.unread?.[userDoc.uid] ||
          (chat.is_expert &&
            userDoc.roles?.support &&
            chat.unread[chat.seller_id])
      : false;
  }, [
    chat.is_expert,
    chat.seller_id,
    chat.unread,
    userDoc?.roles?.support,
    userDoc?.uid,
  ]);

  const getMessageImage = () => {
    const otherParty = chat.seller_id === userDoc?.uid ? buyerDoc : sellerDoc;

    if (
      (userDoc?.roles?.support || userDoc?.roles?.admin) &&
      chat.uids.includes(userDoc.uid)
    ) {
      return (
        <Avatar size="mlarge" user={otherParty ?? undefined} circle={false} />
      );
    }

    if (chat.case_num) {
      return (
        <Avatar
          size="mlarge"
          user={{ uid: process.env.NEXT_PUBLIC_SUPPORT_ID ?? '' }}
          circle={false}
        />
      );
    }

    if (!chat.is_offer && !chat.product_id && otherParty) {
      return <Avatar size="mlarge" user={otherParty} circle={false} />;
    }

    if (orderDoc && productDoc?.has_variations) {
      const item = Object.values(orderDoc.sellers ?? {})
        .flat()
        .find((i) => i.id === productDoc.id);
      if (!item) return null;
      const image =
        productDoc.variations?.find(
          (v) =>
            (!item.variations?.letter || v.size === item.variations?.letter) &&
            (!item.variations?.number || v.size === item.variations?.number) &&
            (!item.variations?.color || v.color === item.variations.color)
        )?.image ?? productDoc.thumbnail;

      return (
        <Link href={`/product/${productDoc.slug}`} target="_blank">
          <div className="relative h-[7rem] w-[7rem] overflow-clip transition-opacity hover:opacity-80">
            <SafeImage
              fill
              src={image}
              alt="product pic"
              key={image}
              className="aspect-square object-cover"
            />
          </div>
        </Link>
      );
    }

    if (!productDoc && userDoc) {
      return (
        <div className="flex gap-x-2">
          <Avatar size="mlarge" user={otherParty ?? undefined} circle={false} />
        </div>
      );
    }

    // Product image for offers or messages about a product
    return (
      <Link href={`/product/${productDoc?.slug}`} target="_blank">
        <div className="relative h-[7rem] w-[7rem] overflow-clip transition-opacity hover:opacity-80">
          <SafeImage
            fill
            src={productDoc?.images?.[0]?.thumb ?? ''}
            alt="product pic"
            key={productDoc?.images?.[0]?.thumb ?? ''}
            className="aspect-square object-cover"
          />
        </div>
      </Link>
    );
  };

  const getLastText = () => {
    if (!chat.messages || chat.messages.length === 0) return 'No messages';
    const messages = chat.messages.filter(
      (a) =>
        !a.is_auto || a.auto_recipient === userDoc?.uid || !a.auto_recipient
    ); // filter out auto messages to the other party
    const lastMessage: ChatMessageDocument | undefined =
      messages[messages.length - 1];
    if (!lastMessage) return 'No messages';
    if (lastMessage.is_img) return 'Image';
    if (lastMessage.uid === userDoc?.uid) {
      return `You: ${lastMessage.content}`;
    }
    return lastMessage.content;
  };

  const getChatTopic = () => {
    if (chat.case_num) {
      return `Support number #${chat.case_num}`;
    }
    if (chat.order_id) {
      return `Order: #${orderDoc?.order_num} ${
        !!orderDoc?.ra_fee && userDoc?.roles?.support ? ' - RA Opted In' : ''
      }`;
    }
    if (chat.is_offer) {
      return `Offer: ${offer && offerStateToText(offer?.state)}`;
    }
    if (chat.product_id) {
      return `Product: ${productDoc?.title}`;
    }
  };

  const lastText = getLastText();

  const getSupportInfo = () => {
    if (!userDoc?.roles?.support) return null;

    const infoItems = [];

    if (sellerDoc?.username) {
      infoItems.push(`S: ${sellerDoc.username}`);
    }

    if (buyerDoc?.username) {
      infoItems.push(`B: ${buyerDoc.username}`);
    }

    return infoItems.length > 0 ? infoItems.join(' • ') : null;
  };

  const FirstTimeBadges = () => {
    if (!userDoc?.roles?.support) return null;

    return (
      <div className="flex flex-wrap items-center gap-2">
        {fullSellerDoc?.uid !== userDoc.uid &&
          fullSellerDoc?.metrics.num_sold === 1 &&
          chat.order_id && (
            <p className="my-2 w-fit overflow-hidden text-ellipsis rounded-full bg-brand-secondary p-2 px-2 text-[1.3rem] text-white lg:whitespace-nowrap">
              FIRST TIME SELLER
            </p>
          )}
        {fullBuyerDoc?.uid !== userDoc.uid &&
          fullBuyerDoc?.metrics.num_purchased === 1 &&
          chat.order_id && (
            <p className="my-2 w-fit overflow-hidden text-ellipsis rounded-full bg-green-800 p-2 px-2 text-[1.3rem] text-white lg:whitespace-nowrap">
              FIRST TIME BUYER
            </p>
          )}
      </div>
    );
  };

  const highlightSearchTerm = (text: string) => {
    if (!searchQuery?.trim() || !text) return text;

    const regex = new RegExp(
      `(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi'
    );

    return text.replace(
      regex,
      '<span class="rounded-2xl font-medium bg-[#FFD480] text-zinc-900 py-2 px-4">$1</span>'
    );
  };

  const supportInfo = getSupportInfo();

  const hasUnreadMessages = chats.some(
    (c) => c.unread !== undefined && Object.values(c.unread).some(Boolean)
  );
  const isChatUnread = chat.unread?.[userDoc?.uid ?? ''];

  return (
    <button
      type="button"
      className={cn(
        'relative flex w-full cursor-pointer items-center justify-between gap-[1rem] py-[1.6rem] text-left hover:bg-brand-lightest-gray ',
        selected && 'bg-brand-lightest-gray pr-[3.1rem]',
        // adding a left padding on the chats that are not unread so the unread dot is aligned with the chat
        hasUnreadMessages && !isChatUnread && 'pl-8',
        isSidebar ? 'sm:pr-[6rem]' : 'sm:pr-[1.6rem]'
      )}
      onClick={() => onClick?.()}
      key={chat.id}
    >
      {isChatUnread && (
        <div className="mb-4 h-4 w-4 rounded-full bg-brand-secondary sm:pb-0" />
      )}

      <div className="flex w-full items-start overflow-hidden px-4">
        <div className={`flex items-center gap-x-4`}>{getMessageImage()}</div>

        <div className="ml-5 w-full space-y-2 overflow-hidden">
          {chat.offer_id && (
            <div className="flex items-center gap-2">
              <div className="h-[2.5rem] w-[6.4rem] rounded-lg bg-brand-secondary px-2 py-1 text-center text-[1.4rem] font-semibold text-white">
                OFFER
              </div>

              {offer && (
                <Chip
                  text={getOfferStatusBadge(offer)}
                  color={getChipColor(
                    offer
                      ? offerStateToText(offer.state).toLowerCase()
                      : 'pending'
                  )}
                  className="!h-[2.5rem] !rounded-lg !text-[1.5rem] !font-semibold"
                />
              )}
            </div>
          )}

          <h1 className="line-clamp-2 w-full overflow-hidden text-ellipsis text-[1.5rem] font-semibold lg:whitespace-nowrap">
            <div className="flex w-full justify-between">
              <div className="flex items-center gap-2">
                <span
                  className="max-w-[40rem] truncate"
                  dangerouslySetInnerHTML={{
                    __html: highlightSearchTerm(getTitle() || ''),
                  }}
                />

                {chat.flagged && (
                  <FlagIcon className="h-8 w-8 text-brand-secondary" />
                )}
              </div>

              <span
                className={
                  'whitespace-nowrap text-[1.4rem] font-light text-zinc-400'
                }
              >
                {timeAgo(chat.last_time)}
              </span>
            </div>

            {chat.is_expert && (
              <span className="flex items-center gap-2 text-brand-gray">
                Expert <CurationsIcon />
              </span>
            )}
          </h1>

          {isProductLoading ? (
            <div className="h-[1.5rem] w-[10rem] animate-pulse rounded-xl bg-zinc-200" />
          ) : (
            <div className="space-y-1">
              {!chat.offer_id && (
                <p className="w-full truncate text-[1.4rem] text-brand-gray lg:whitespace-nowrap">
                  {getChatTopic()}
                </p>
              )}

              {productDoc && !chat.is_offer && !chat.order_id && (
                <ProductPriceRange
                  product={productDoc}
                  className="!text-[1.3rem] !font-semibold !text-zinc-500"
                />
              )}

              <FirstTimeBadges />

              {supportInfo && (
                <p className="text-[1.2rem] font-medium text-zinc-500">
                  {supportInfo}
                </p>
              )}
            </div>
          )}

          <div className="my-2 flex w-full items-center justify-between gap-4">
            <div className="flex w-full items-center gap-x-2">
              {lastText === 'Image' && (
                <ImageIcon className="h-8 w-8 text-zinc-500" />
              )}

              {offer && offer.state === 0 ? (
                <div className="flex w-full truncate">
                  <span
                    className={`my-2 line-clamp-2 max-w-[40rem] text-[1.5rem] text-brand-gray ${
                      isUnread ? 'font-medium' : ''
                    }`}
                  >
                    Expires in {formatTimeRemaining(offer)}
                  </span>
                </div>
              ) : (
                <div className="flex w-full items-center justify-between">
                  <span
                    className={`my-2 line-clamp-2 max-w-[50rem] text-[1.5rem] text-zinc-400 ${
                      isUnread ? 'font-medium' : ''
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: highlightSearchTerm(lastText),
                    }}
                  />

                  {chat.favorited?.includes(userDoc?.uid ?? '') && (
                    <div className="flex justify-end pr-4">
                      <StarIcon className="h-6 w-6 fill-zinc-600 text-zinc-600" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

export default MessagePreview;
